// src/services/staleness.js
// Shared staleness utilities for all data types.
//
// Mirrors the decay thresholds in functions/intelligence/temporal.js
// so the UI shows staleness warnings at exactly the same thresholds
// the intelligence engine uses when scoring confidence.
//
// Threshold tiers per type (matches DECAY_PARAMS halfLifeMin × 2):
//   sos:          stale after  60 min  (halfLife 30min)
//   gas:          stale after 120 min  (halfLife 60min)
//   connectivity: stale after 180 min  (halfLife 90min)
//   flood:        stale after 240 min  (halfLife 120min)
//   road:         stale after 360 min  (halfLife 180min)
//   shelter:      stale after 480 min  (halfLife 240min)
//   power:        stale after 480 min  (halfLife 240min)
//   gas_station:  stale after  90 min  (critical resource)
//   shelter_avail stale after  60 min  (capacity changes fast)
//   medical_res:  stale after 180 min  (hospital status)
//   charging:     stale after 120 min
//   supply_dist:  stale after  60 min  (supply depletes)
//   nws_alert:    stale after 720 min  (12h — NWS alerts expire by design)
//
// Two warning tiers:
//   "aging"  — past halfLife but not yet stale (show amber warning)
//   "stale"  — past 2× halfLife (show grey, reduced opacity)

export const STALENESS_PARAMS = {
  sos:          { halfLifeMin:  30, label: 'SOS report' },
  gas:          { halfLifeMin:  60, label: 'Gas status' },
  connectivity: { halfLifeMin:  90, label: 'WiFi status' },
  flood:        { halfLifeMin: 120, label: 'Flood report' },
  road:         { halfLifeMin: 180, label: 'Road condition' },
  shelter:      { halfLifeMin: 240, label: 'Shelter status' },
  power:        { halfLifeMin: 240, label: 'Power status' },
  nws_alert:    { halfLifeMin: 360, label: 'Weather alert' },
  default:      { halfLifeMin: 120, label: 'Report' },
};

// Returns null | 'aging' | 'stale'
export function getStaleness(type, tsMs) {
  if (!tsMs) return null;
  const params   = STALENESS_PARAMS[type] ?? STALENESS_PARAMS.default;
  const ageMin   = (Date.now() - tsMs) / 60_000;

  if (ageMin > params.halfLifeMin * 2) return 'stale';
  if (ageMin > params.halfLifeMin)     return 'aging';
  return null;
}

// Human-readable staleness label for tooltip/aria
export function getStalenessLabel(type, tsMs) {
  if (!tsMs) return null;
  const params   = STALENESS_PARAMS[type] ?? STALENESS_PARAMS.default;
  const ageMin   = Math.round((Date.now() - tsMs) / 60_000);
  const ageLabel = ageMin < 60
    ? `${ageMin} min`
    : `${Math.round(ageMin / 60)} hr`;

  const level = getStaleness(type, tsMs);
  if (!level) return null;

  if (level === 'stale') {
    return `${params.label} is ${ageLabel} old — conditions may have changed`;
  }
  return `${params.label} is ${ageLabel} old — verify before acting`;
}
